import { useSession } from "@/lib/auth-client";
import { orpc } from "@/lib/orpc";
import { ReviewPanel } from "@/components/template/review-panel";
import { EnvironmentSelector } from "@/components/project/environment-selector";
import { ReviewChanges } from "@/components/template/review-changes";
import { flagLocales } from "@/utils/flag-locales";
import { ReviewPreview } from "@/components/template/review-preview";
import { PublishDialog } from "@/components/template/publish-dialog";
import { AuditTimeline } from "@/components/template/audit-timeline";
import { TemplateErrorComponent } from "@/components/error";
import { toError } from "@/lib/to-error";
import { PendingTemplateComponent } from "@/components/pending";
import { Badge } from "@papier/ui/components/ui/badge";
import { Button } from "@papier/ui/components/ui/button";
import { Label } from "@papier/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@papier/ui/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@papier/ui/components/ui/tabs";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Pen as Edit } from "@papier/ui/components/icons";
import { useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_app/projects/$projectSlug/templates/$templateSlug/review")({
  validateSearch: z.object({ env: z.string().optional() }),
  wrapInSuspense: true,
  pendingComponent: PendingTemplateComponent,
  errorComponent: ({ error }) => <TemplateErrorComponent error={toError(error)} />,
  loader: async ({ context: { queryClient }, params: { projectSlug, templateSlug } }) => {
    const project = await queryClient.ensureQueryData(
      orpc.projects.getBySlug.queryOptions({ input: { projectSlug } }),
    );
    // Always refresh on entry: an editor save may have invalidated an earlier approval.
    const draft = await queryClient.fetchQuery({
      ...orpc.templates.getDraft.queryOptions({ input: { projectId: project.id, templateSlug } }),
      staleTime: 0,
    });
    await queryClient.fetchQuery({
      ...orpc.templates.getReviewPolicy.queryOptions({ input: { projectId: project.id } }),
      staleTime: 0,
    });
    return { project, name: draft.name };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: `Papier - Review ${loaderData?.name ?? "template"}` }],
  }),
  component: ReviewTemplate,
});

function ReviewTemplate() {
  const { project } = Route.useLoaderData();
  const { projectSlug, templateSlug } = Route.useParams();
  const { env } = Route.useSearch();
  const { Link } = Route;
  const [view, setView] = useState("preview");
  const [locale, setLocale] = useState<string | null>(null);
  const [environment, setEnvironment] = useState(env ?? project.environments[0]?.slug ?? "");
  const { data: session } = useSession();
  const { data: policy } = useSuspenseQuery({
    ...orpc.templates.getReviewPolicy.queryOptions({ input: { projectId: project.id } }),
    refetchInterval: 10_000,
  });
  const { data: draft } = useSuspenseQuery({
    ...orpc.templates.getDraft.queryOptions({ input: { projectId: project.id, templateSlug } }),
    refetchInterval: 10_000,
  });
  const {
    data: published,
    isPending: loadingPublished,
    error: publishedError,
    refetch: retryPublished,
  } = useQuery({
    ...orpc.templates.getPublishedTemplate.queryOptions({
      input: { projectId: project.id, templateSlug, environmentSlug: environment },
    }),
    enabled: draft.status === "published" && !!environment,
  });
  const selectedLocale =
    draft.content?.locales.find((l) => l.locale === locale) ?? draft.content?.locales[0];
  const liveLocale = published?.localeContent.find((l) => l.locale === selectedLocale?.locale);
  const compactPreview = draft.type === "sms" || draft.type === "push_notification";
  const languageControl = (
    <div className="flex min-w-0 items-center gap-2">
      <Label htmlFor="review-locale" className="shrink-0 text-xs text-muted-foreground">
        Language
      </Label>
      <Select value={selectedLocale?.locale} onValueChange={setLocale}>
        <SelectTrigger id="review-locale" className="w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {draft.content?.locales.map((item) => (
            <SelectItem key={item.locale} value={item.locale}>
              {flagLocales.find((entry) => entry.code === item.locale)?.name ?? item.locale}
              {!item.translatedAt ? " · not translated" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  return (
    <main className="mx-auto w-full max-w-[1600px] min-w-0 space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b pb-6">
        <div className="min-w-0 space-y-2">
          <Link
            to="/projects/$projectSlug"
            params={{ projectSlug }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            {project.name}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight break-words">{draft.name}</h1>
            <Badge variant="secondary">
              {draft.hasUnpublishedChanges ? "Unpublished changes" : "Up to date"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Review the draft, check what changed, and choose the next step.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link
              to="/projects/$projectSlug/templates/$templateSlug/edit"
              params={{ projectSlug, templateSlug }}
            >
              <Edit />
              Edit draft
            </Link>
          </Button>
          <Button className="lg:hidden" variant="secondary" asChild>
            <a href="#review-decision">
              {policy.mode === "open" ? "Publishing" : "Review decision"}
              <ArrowRight />
            </a>
          </Button>
        </div>
      </header>
      <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] xl:gap-8">
        <Tabs value={view} onValueChange={setView} className="min-w-0 gap-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList variant="floating" aria-label="Review views">
              <TabsTrigger value="preview">Preview</TabsTrigger>
              <TabsTrigger value="changes" disabled={draft.status !== "published"}>
                Changes
              </TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>
            {view !== "activity" && languageControl}
          </div>
          <TabsContent value="preview" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-medium">Current draft</h2>
              <span className="text-xs text-muted-foreground">
                {draft.content?.locales.length ?? 0} languages included in this review
              </span>
            </div>
            {draft.status !== "published" && (
              <p className="text-sm text-muted-foreground">
                This is the first release. Changes will be available to compare after publishing.
              </p>
            )}
            <ReviewPreview
              compact={compactPreview}
              content={selectedLocale?.content}
              label="Proposed draft preview"
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              This is the draft customers will receive after publishing. Check each language; data
              fields appear as placeholders.
              {policy.mode !== "open" && " Editing the draft after approval requires a new review."}
            </p>
          </TabsContent>
          <TabsContent value="changes" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-medium">What changed</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Compare the current draft with the release your customers receive.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="review-environment"
                  className="shrink-0 text-xs text-muted-foreground"
                >
                  Live in
                </Label>
                <EnvironmentSelector
                  id="review-environment"
                  currentEnv={environment}
                  environments={project.environments}
                  showAllOption={false}
                  setEnvironment={(value) => {
                    if (value) setEnvironment(value);
                  }}
                />
              </div>
            </div>
            {publishedError ? (
              <div role="alert" className="rounded-xl border p-4">
                <p className="mb-3 text-sm text-destructive">
                  The live revision could not be loaded.
                </p>
                <Button variant="outline" size="sm" onClick={() => void retryPublished()}>
                  Retry loading
                </Button>
              </div>
            ) : loadingPublished ? (
              <p role="status">Loading live revision…</p>
            ) : (
              <>
                <ReviewChanges
                  before={liveLocale?.content}
                  after={selectedLocale?.content}
                  beforeSchema={published?.schemaDefinition}
                  afterSchema={draft.schema?.schemaDefinition}
                />
                <div className="grid min-w-0 gap-4 xl:grid-cols-2">
                  <section className="min-w-0 space-y-3" aria-label="Live revision">
                    <h3 className="text-sm font-medium">
                      Live {published ? `· v${published.version}` : ""}
                    </h3>
                    <ReviewPreview
                      compact={compactPreview}
                      content={liveLocale?.content}
                      label="Live release preview"
                    />
                  </section>
                  <section className="min-w-0 space-y-3" aria-label="Proposed revision">
                    <h3 className="text-sm font-medium">Draft · proposed</h3>
                    <ReviewPreview
                      compact={compactPreview}
                      content={selectedLocale?.content}
                      label="Proposed draft preview"
                    />
                  </section>
                </div>
              </>
            )}
            <details className="rounded-xl border p-4">
              <summary className="cursor-pointer text-sm font-medium">
                Data schema and source
              </summary>
              <p className="my-3 text-xs text-muted-foreground">
                Approval covers all languages and the data schema.
              </p>
              <div className="grid gap-4 xl:grid-cols-2">
                {[
                  {
                    title: "Live source",
                    value: { schema: published?.schemaDefinition, content: liveLocale?.content },
                  },
                  {
                    title: "Draft source",
                    value: {
                      schema: draft.schema?.schemaDefinition,
                      content: selectedLocale?.content,
                    },
                  },
                ].map((item) => (
                  <div key={item.title} className="min-w-0">
                    <h3 className="mb-2 text-xs font-medium">{item.title}</h3>
                    <pre className="max-h-80 overflow-auto rounded-lg bg-muted/40 p-3 font-mono text-xs break-all whitespace-pre-wrap">
                      {JSON.stringify(item.value, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </details>
          </TabsContent>
          <TabsContent value="activity">
            <AuditTimeline templateId={draft.id} />
          </TabsContent>
        </Tabs>
        <aside id="review-decision" className="min-w-0 scroll-mt-6 space-y-4 lg:sticky lg:top-6">
          <ReviewPanel
            projectId={project.id}
            templateId={draft.id}
            draftContentId={draft.contentId ?? null}
            expectedRevision={draft.reviewRevision ?? undefined}
            hasUnpublishedChanges={draft.hasUnpublishedChanges}
            currentUserId={session?.user.id}
            publishAction={
              <PublishDialog
                project={project}
                templateSlug={templateSlug}
                availableLocales={draft.content?.locales ?? []}
                label={policy.mode === "open" ? "Publish" : "Publish approved revision"}
              />
            }
          />
          <p className="px-2 text-xs leading-relaxed text-muted-foreground">
            {policy.mode === "open"
              ? "No approval is required for this project. "
              : `${policy.requiredApprovals} distinct approval${policy.requiredApprovals === 1 ? "" : "s"} required. `}
            <Link
              to="/projects/$projectSlug/settings"
              params={{ projectSlug }}
              hash="approvals"
              className="underline underline-offset-4"
            >
              Approval settings
            </Link>
          </p>
        </aside>
      </div>
    </main>
  );
}
